import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, CalendarDays, Bot, CheckCircle2, Clock, FileText, Receipt, CreditCard, FileDown } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { NexusChat } from "@/components/NexusChat";

const REQUEST_STEPS = ["Submitted", "Reviewing", "Approved", "Scheduled", "In Progress", "Ready for Pickup", "Completed"];
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function Portal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["portal"], queryFn: () => api.get("/portal/summary").then(r => r.data) });
  const { data: billing } = useQuery({ queryKey: ["portal-billing"], queryFn: () => api.get("/portal/billing").then(r => r.data) });
  const { data: dl } = useQuery({ queryKey: ["portal-downloads"], queryFn: () => api.get("/downloads/my").then(r => r.data) });
  const { data: pw } = useQuery({ queryKey: ["portal-work"], queryFn: () => api.get("/portal/work").then(r => r.data) });
  const doLogout = async () => { await logout(); nav("/customer/login"); };

  const payInvoice = async (inv) => {
    try {
      const res = await api.post("/payments/invoice/checkout", { invoiceId: inv.id, email: user.email, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const respondEstimate = async (id, decision) => {
    try { await api.put(`/portal/estimates/${id}/respond`, { decision }); qc.invalidateQueries({ queryKey: ["portal-billing"] }); toast.success(`Estimate ${decision}d`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="tdw-navy-bg text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo variant="wordmark-light" />
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-300 hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 gap-1.5" onClick={doLogout} data-testid="portal-logout"><LogOut className="h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-slate-500 mt-1">Track your requests, appointments, and get help from Nexus anytime.</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard icon={Inbox} label="Service Requests" value={data?.requests?.length ?? 0} />
          <StatCard icon={CalendarDays} label="Appointments" value={data?.appointments?.length ?? 0} />
          <Link to="/chat" className="rounded-xl bg-gradient-to-br from-electric to-tech text-white p-5 flex items-center justify-between hover:opacity-95 transition-opacity" data-testid="portal-chat-link">
            <div><p className="font-heading font-semibold">Ask Nexus</p><p className="text-sm text-white/80">Get instant help</p></div><Bot className="h-8 w-8" />
          </Link>
        </div>

        <section>
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">Your Service Requests</h2>
          {(data?.requests || []).length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-8 text-center text-slate-400" data-testid="portal-no-requests">
              No requests yet. <Link to="/request-service" className="text-electric">Submit one</Link>.
            </div>
          ) : (
            <div className="space-y-4">
              {(data?.requests || []).map((r) => (
                <div key={r.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`portal-request-${r.id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div><p className="font-heading font-semibold text-navy">{r.serviceType}</p><p className="text-xs text-slate-500 font-mono">{r.requestNumber}</p></div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-electric/10 text-electric">{r.status}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{r.description}</p>
                  <Timeline current={r.status} />
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">Your Appointments</h2>
          {(data?.appointments || []).length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-8 text-center text-slate-400">No appointments yet. <Link to="/book" className="text-electric">Book one</Link>.</div>
          ) : (
            <div className="space-y-3">
              {(data?.appointments || []).map((a) => (
                <div key={a.id} className="rounded-xl bg-white border border-slate-200 p-4 flex justify-between items-center" data-testid={`portal-appt-${a.id}`}>
                  <div><p className="font-medium text-navy">{a.serviceType}</p><p className="text-xs text-slate-500">{a.requestedDate ? new Date(a.requestedDate).toLocaleString() : ""} · {a.locationType}</p></div>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{a.status}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">Estimates</h2>
          {(billing?.estimates || []).length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-6 text-center text-slate-400 text-sm">No estimates yet.</div>
          ) : (
            <div className="space-y-3">
              {(billing?.estimates || []).map((e) => (
                <div key={e.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`portal-estimate-${e.id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-electric" /><div><p className="font-medium text-navy">{e.estimateNumber}</p><p className="text-xs text-slate-500">{money(e.total)}</p></div></div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{e.status}</span>
                  </div>
                  {e.status === "sent" && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="bg-electric hover:bg-tech" onClick={() => respondEstimate(e.id, "approve")} data-testid={`approve-estimate-${e.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => respondEstimate(e.id, "reject")} data-testid={`reject-estimate-${e.id}`}>Decline</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-navy">Invoices</h2>
            {(billing?.invoices || []).length > 0 && (
              <a href={`${API}/portal/statement/pdf`} target="_blank" rel="noreferrer" data-testid="portal-statement-btn">
                <Button size="sm" variant="outline" className="gap-1.5"><FileDown className="h-4 w-4" /> Download Statement</Button>
              </a>
            )}
          </div>
          {(billing?.invoices || []).length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-6 text-center text-slate-400 text-sm">No invoices yet.</div>
          ) : (
            <div className="space-y-3">
              {(billing?.invoices || []).map((inv) => (
                <div key={inv.id} className="rounded-xl bg-white border border-slate-200 p-5 flex justify-between items-center flex-wrap gap-3" data-testid={`portal-invoice-${inv.id}`}>
                  <div className="flex items-center gap-2"><Receipt className="h-4 w-4 text-electric" /><div><p className="font-medium text-navy">{inv.invoiceNumber}</p><p className="text-xs text-slate-500">Balance {money(inv.balance)} of {money(inv.total)}</p></div></div>
                  <div className="flex gap-2 items-center">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{inv.status.replace("_", " ")}</span>
                    {inv.balance > 0 ? (
                      <Button size="sm" className="bg-electric hover:bg-tech gap-1.5" onClick={() => payInvoice(inv)} data-testid={`portal-pay-${inv.id}`}><CreditCard className="h-4 w-4" /> Pay</Button>
                    ) : (
                      <a href={`${API}/invoices/${inv.id}/receipt?email=${encodeURIComponent(user.email)}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1.5"><FileDown className="h-4 w-4" /> Receipt</Button></a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">Repair Jobs</h2>
          {(pw?.jobs || []).length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-6 text-center text-slate-400 text-sm">No repair jobs yet.</div>
          ) : (
            <div className="space-y-4">
              {(pw?.jobs || []).map((j) => (
                <div key={j.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`portal-job-${j.id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div><p className="font-heading font-semibold text-navy">{j.device}</p><p className="text-xs text-slate-500 font-mono">{j.jobNumber}</p></div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-electric/10 text-electric">{j.status}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{j.reportedIssue}</p>
                  {j.customerNotes && <p className="mt-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{j.customerNotes}</p>}
                  <StepTimeline steps={pw?.jobStatuses || []} current={j.status} />
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">Projects</h2>
          {(pw?.projects || []).length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-6 text-center text-slate-400 text-sm">No projects yet.</div>
          ) : (
            <div className="space-y-4">
              {(pw?.projects || []).map((p) => {
                const idx = (pw?.projectStatuses || []).indexOf(p.status);
                const pct = idx >= 0 ? Math.round((idx + 1) / (pw?.projectStatuses || []).length * 100) : 0;
                return (
                  <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`portal-project-${p.id}`}>
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <div><p className="font-heading font-semibold text-navy">{p.projectType} Project</p><p className="text-xs text-slate-500 font-mono">{p.projectNumber}</p></div>
                      <span className="text-xs px-2.5 py-1 rounded-full bg-electric/10 text-electric">{p.status}</span>
                    </div>
                    {p.customerVisibleProgress && <p className="mt-2 text-sm text-slate-600">{p.customerVisibleProgress}</p>}
                    <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-electric to-tech" style={{ width: `${pct}%` }} /></div>
                    {(p.milestones || []).length > 0 && (
                      <ul className="mt-3 space-y-1">{p.milestones.map((m, i) => (
                        <li key={i} className={`text-sm flex items-center gap-2 ${m.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{m.done ? <CheckCircle2 className="h-4 w-4 text-electric" /> : <Clock className="h-4 w-4 text-slate-300" />} {m.title}</li>
                      ))}</ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">My Downloads</h2>
          {(dl?.purchases || []).length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-6 text-center text-slate-400 text-sm">No purchases yet. <Link to="/digital-downloads" className="text-electric">Browse guides</Link>.</div>
          ) : (
            <div className="space-y-3">
              {(dl?.purchases || []).map((pur) => (
                <div key={pur.id} className="rounded-xl bg-white border border-slate-200 p-5 flex justify-between items-center flex-wrap gap-3" data-testid={`portal-download-${pur.id}`}>
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-electric" /><div><p className="font-medium text-navy">{pur.productTitle}</p><p className="text-xs text-slate-500">{money(pur.amount)} · {pur.status}</p></div></div>
                  {pur.status === "completed" ? (
                    <a href={`${API}/downloads/access/${pur.token}`} target="_blank" rel="noreferrer"><Button size="sm" className="bg-electric hover:bg-tech gap-1.5" data-testid={`download-${pur.id}`}><FileDown className="h-4 w-4" /> Download</Button></a>
                  ) : <span className="text-xs text-slate-400">Unavailable</span>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">Nexus Assistant</h2>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm max-w-lg">
            <NexusChat source="portal" />
          </div>
        </section>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <span className="h-9 w-9 rounded-lg bg-electric/10 text-electric grid place-items-center"><Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /></span>
      <p className="mt-3 text-3xl font-heading font-bold text-navy">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

function StepTimeline({ steps, current }) {
  const idx = steps.indexOf(current);
  const activeIdx = idx === -1 ? 0 : idx;
  return (
    <div className="mt-4 flex items-center overflow-x-auto" data-testid="job-timeline">
      {steps.map((step, i) => (
        <div key={step} className="flex items-center shrink-0">
          <div className="flex flex-col items-center">
            <div className={`h-6 w-6 rounded-full grid place-items-center text-[10px] ${i <= activeIdx ? "bg-electric text-white" : "bg-slate-200 text-slate-400"}`}>
              {i < activeIdx ? <CheckCircle2 className="h-3.5 w-3.5" /> : i === activeIdx ? <Clock className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={`mt-1 text-[9px] whitespace-nowrap ${i <= activeIdx ? "text-navy font-medium" : "text-slate-400"}`}>{step}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-6 h-0.5 mx-0.5 mb-4 ${i < activeIdx ? "bg-electric" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
}

function Timeline({ current }) {
  const idx = REQUEST_STEPS.indexOf(current);
  const activeIdx = idx === -1 ? 0 : idx;
  return (
    <div className="mt-4 flex items-center overflow-x-auto" data-testid="repair-timeline">
      {REQUEST_STEPS.map((step, i) => (
        <div key={step} className="flex items-center shrink-0">
          <div className="flex flex-col items-center">
            <div className={`h-6 w-6 rounded-full grid place-items-center text-[10px] ${i <= activeIdx ? "bg-electric text-white" : "bg-slate-200 text-slate-400"}`}>
              {i < activeIdx ? <CheckCircle2 className="h-3.5 w-3.5" /> : i === activeIdx ? <Clock className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={`mt-1 text-[9px] whitespace-nowrap ${i <= activeIdx ? "text-navy font-medium" : "text-slate-400"}`}>{step}</span>
          </div>
          {i < REQUEST_STEPS.length - 1 && <div className={`w-8 h-0.5 mx-0.5 mb-4 ${i < activeIdx ? "bg-electric" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
}
